import { RangeValue } from 'rc-picker/lib/interface';
import moment, { Moment } from 'moment';

const DATE_FORMAT = 'YYYY-MM-DD';

/** Longest period the analytics toolbar lets a reader select, in days with
 * both ends counted. Counted in days rather than months because 12 calendar
 * months can span 367 days across a leap day, and the API refuses a daily
 * series longer than 366 (`MAX_DAILY_RANGE_DAYS`), which the line cards ask
 * for. */
export const MAX_PERIOD_DAYS = 365;

/** Inclusive ISO `YYYY-MM-DD` bounds, the shape a resolved analytics period and
 * a quick-select capsule range both already have. */
export interface DateRange {
    start: string;
    end: string;
}

/**
 * Is `current` outside the longest selectable period, given the bound already
 * placed in the range picker?
 *
 * antd v4's `disabledDate` is told the cell it is judging and nothing else, so
 * the caller has to feed it the in-progress selection from `onCalendarChange`
 * and reset that on `onOpenChange`. Both bounds are closed over, since a reader
 * can place either end of the range first.
 */
export const isOutsideMaxPeriod = (
    current: Moment,
    picking: RangeValue<Moment>,
    days = MAX_PERIOD_DAYS
): boolean => {
    if (!picking) return false;
    const [start, end] = picking;
    const tooLate =
        !!start && current.isAfter(start.clone().add(days - 1, 'days'), 'day');
    const tooEarly =
        !!end &&
        current.isBefore(end.clone().subtract(days - 1, 'days'), 'day');
    return tooLate || tooEarly;
};

/**
 * Longest legal period ending where `range` ends: the start moves forward, the
 * end never does.
 */
export const clampToMaxPeriod = (
    range: DateRange,
    days = MAX_PERIOD_DAYS
): DateRange => {
    const earliestStart = moment(range.end, DATE_FORMAT).subtract(
        days - 1,
        'days'
    );
    if (!moment(range.start, DATE_FORMAT).isBefore(earliestStart)) {
        return range;
    }
    return { start: earliestStart.format(DATE_FORMAT), end: range.end };
};

/**
 * Generic handler for date picker onChange events
 * Only triggers the callback when both dates are selected or when dates are cleared
 *
 * @param dates - The date range from the picker
 * @param onValidDates - Callback when both dates are selected
 * @param onClear - Optional callback when dates are cleared
 */
export const handleDatePickerChange = (
    dates: RangeValue<Moment>,
    onValidDates: (dates: [Moment, Moment]) => void,
    onClear?: () => void
) => {
    if (dates && dates[0] && dates[1]) {
        // Both dates are selected - trigger callback with valid dates
        onValidDates([dates[0], dates[1]]);
    } else if (!dates && onClear) {
        // Dates were cleared - trigger optional clear callback
        onClear();
    }
    // If only one date is selected, do nothing (no API calls)
};

/**
 * Generic handler for updating URL parameters with dates
 *
 * @param dates - The date range from the picker
 * @param updateParams - Function to update URL parameters
 * @param format - Optional date format (default: 'YYYY-MM-DD')
 */
export const handleDateParamsUpdate = (
    dates: RangeValue<Moment>,
    updateParams: (params: { startDate?: string; endDate?: string }) => void,
    format = DATE_FORMAT
) => {
    handleDatePickerChange(
        dates,
        (validDates) => {
            updateParams({
                startDate: validDates[0].format(format),
                endDate: validDates[1].format(format),
            });
        },
        () => {
            updateParams({
                startDate: undefined,
                endDate: undefined,
            });
        }
    );
};
/**
 * Check if date range parameters are valid for API calls
 *
 * @param dateParams - Object containing startDate and endDate
 * @returns true if both dates are present and valid
 */
export const isValidDateRange = (dateParams: {
    startDate?: string;
    endDate?: string;
}): boolean => {
    return !!(dateParams.startDate && dateParams.endDate);
};
